import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { throwError } from 'rxjs';

import { ConfigurationService, InfraPreset, Logger } from '@mobi/rwc-utils-common-ng-jslib';

import { AUTH_INFO_PROVIDER } from '../auth-provider';

export const AUTHORIZATION_HEADER = 'authorization';

@Injectable({ providedIn: 'root' })
export class AccessTokenErrorHandlerService {
    #isCip = inject(AUTH_INFO_PROVIDER, { optional: true }) === 'CIP';
    #logger = inject(Logger).createCustomLogger('AccessTokenInterceptor');
    #infraPresetAudAndScope = getAudAndScope(inject(ConfigurationService).getSync('infraPreset') as InfraPreset);

    handleAccessTokenError(errorResponse: HttpErrorResponse, request: HttpRequest<unknown>) {
        if (errorResponse.status === 401) {
            return this.#handle401(errorResponse, request);
        }
        return throwError(() => errorResponse);
    }

    #handle401(errorResponse: HttpErrorResponse, req: HttpRequest<unknown>) {
        const token = errorResponse.headers?.get(AUTHORIZATION_HEADER) ?? req.headers.get(AUTHORIZATION_HEADER);
        if (!token) {
            this.#logger.error(`401 Error - Request to ${errorResponse.url}: Access token is missing`);
        } else {
            const decodedToken = jwtDecode<CipToken>(token);
            if (decodedToken.exp && decodedToken.exp * 1000 < Date.now()) {
                this.#logger.error(`401 Error - Request to ${errorResponse.url}: Access token is invalid`);
            } else {
                const cipError = validateCipAudAndScope(decodedToken, this.#infraPresetAudAndScope);
                if (this.#isCip && cipError) {
                    this.#logger.error(`401 Error - Request to ${errorResponse.url}: ${cipError.message}`);
                    return throwError(() => cipError);
                } else {
                    this.#logger.error(
                        `401 Error - Request to ${errorResponse.url}: Access token seems valid: '${token}'`
                    );
                }
            }
        }
        return throwError(() => errorResponse);
    }
}

function validateCipAudAndScope(decodedToken: CipToken, expectedAudAndScope: ValidAudAndScope): void | CipError {
    const actualScopes = decodedToken.scope?.split(' ') ?? [];
    const actualCommunicationRelation = decodedToken.communication_relation;
    const actualAud =
        decodedToken.aud && !Array.isArray(decodedToken.aud)
            ? [decodedToken.aud]
            : (decodedToken.aud as string[] | undefined);
    if (
        (actualCommunicationRelation && !actualScopes.includes(actualCommunicationRelation.toLowerCase())) ||
        !actualAud?.includes(expectedAudAndScope.aud) ||
        !actualScopes.includes(expectedAudAndScope.scope)
    ) {
        return new CipError(
            actualCommunicationRelation,
            actualScopes,
            actualAud,
            expectedAudAndScope.scope,
            expectedAudAndScope.aud
        );
    }
}

function getAudAndScope(infraPreset: InfraPreset): ValidAudAndScope {
    switch (infraPreset) {
        case 'b2b':
            return { aud: 'caz-b2c', scope: 'b2b' };
        case 'b2c':
            return { aud: 'caz-b2c', scope: 'b2c' };
        case 'b2e':
        case 'b2eadmin':
            return { aud: 'caz-b2e', scope: 'b2e' };
        default:
            throw new Error(`Unsupported infraPreset: ${infraPreset}`);
    }
}

interface ValidAudAndScope {
    aud: 'caz-b2c' | 'caz-b2e';
    scope: 'b2b' | 'b2c' | 'b2e';
}

interface CipToken {
    aud?: string[] | string;
    scope: string;
    communication_relation: 'B2B' | 'B2C' | 'B2E';
    exp: number;
}

export class CipError extends Error {
    override readonly name = 'CipError';
    readonly communicationRelation: CipToken['communication_relation'];
    readonly actualScopes: string[];
    readonly actualAud: string[] | undefined;
    readonly expectedScope: ValidAudAndScope['scope'];
    readonly expectedAud: ValidAudAndScope['aud'];

    constructor(
        communicationRelation: CipToken['communication_relation'],
        actualScopes: string[],
        actualAud: string[] | undefined,
        expectedScope: ValidAudAndScope['scope'],
        expectedAud: ValidAudAndScope['aud']
    ) {
        super(
            `Access token validation failed:
  - Expected field 'aud=${actualAud?.join(', ') ?? ''}' to contain '${expectedAud}' audience
  - Expected field 'scope=${actualScopes.join(' ') || ''}' to contain '${expectedScope}' scope
  - Expected field 'communication_relation=${communicationRelation ?? ''}' to be include in scope '${actualScopes.join(' ') || ''}'
Please logout and login again to refresh your token.`
        );
        this.communicationRelation = communicationRelation;
        this.actualScopes = actualScopes;
        this.actualAud = actualAud;
        this.expectedScope = expectedScope;
        this.expectedAud = expectedAud;
    }
}
